import ContentContainerBox from "components/stocks/ContentContainerBox";
import type { IYouTubeVideoFieldsFragment } from "lib/fragments/__generated__/YouTubeVideoFields.generated";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

interface IYouTubeVideosProps {
  youtubeVideos: IYouTubeVideoFieldsFragment[];
}

export default function YouTubeVideos({ youtubeVideos }: IYouTubeVideosProps) {
  const [currentVideo, setCurrentVideo] = useState(() => {
    if (youtubeVideos.length > 0) {
      return youtubeVideos[0];
    }
    return null;
  });

  if (!currentVideo) {
    return null;
  }

  const otherVideos = currentVideo.position === 1 ? youtubeVideos.slice(1) : youtubeVideos;

  return (
    <ContentContainerBox className="relative mx-auto max-w-2xl">
      <div className="absolute -top-4 left-6 flex rounded-lg bg-white p-2 shadow">
        <YoutubeIcon />
      </div>

      <div>
        <div className="mt-2">
          <h2 title={currentVideo.title} className="m-0 flex font-aeonik text-xl font-bold text-gray-800">
            <div className="line-clamp-1 text-ellipsis whitespace-nowrap">{currentVideo.title}</div>
          </h2>
        </div>

        <div className="mt-1">
          <p className="m-0 font-aeonik text-sm font-medium text-neutral-500">
            {"por "}
            <a
              href={currentVideo.author.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              <em>{currentVideo.author.name}</em>
            </a>
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <div className="relative h-0 rounded-lg pb-[56.25%] pt-6">
            <iframe
              title={currentVideo.title}
              className="absolute left-0 top-0 h-full w-full rounded-lg"
              allowFullScreen={true}
              src={getEmbedUrl(currentVideo)}
              frameBorder="0"
            />
          </div>
        </div>

        {otherVideos.length > 0 && (
          <div>
            <div className="flex items-center space-x-3">
              <strong className="whitespace-nowrap font-aeonik text-sm font-bold uppercase text-neutral-500">
                Outros Vídeos ({otherVideos.length})
              </strong>

              <div className="h-1 w-full rounded-full bg-neutral-100" />
            </div>

            <div className="mt-4">
              <YouTubeVideoCarousel videos={otherVideos} onSelectVideo={setCurrentVideo} />
            </div>
          </div>
        )}
      </div>
    </ContentContainerBox>
  );
}

function getEmbedUrl(video: IYouTubeVideoFieldsFragment) {
  try {
    const url = new URL(video.url);
    if (url.searchParams.has("t")) {
      const startTime = url.searchParams.get("t");
      return `https://www.youtube-nocookie.com/embed/${video.id}?rel=0&start=${startTime}`;
    }
  } catch {} // eslint-disable-line
  return `https://www.youtube-nocookie.com/embed/${video.id}?rel=0`;
}

function YoutubeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={80} height={18} viewBox="0 0 80 18">
      <g fillRule="nonzero" fill="none">
        <path
          fill="red"
          d="M24.968 2.867a3.201 3.201 0 00-2.25-2.265C20.736.07 12.782.07 12.782.07s-7.95 0-9.937.533a3.202 3.202 0 00-2.25 2.264C.066 4.863.066 9.03.066 9.03s0 4.168.53 6.164a3.205 3.205 0 002.25 2.265c1.987.533 9.937.533 9.937.533s7.954 0 9.936-.533a3.203 3.203 0 002.25-2.265c.53-1.996.53-6.163.53-6.163s0-4.168-.53-6.164z"
        />
        <path fill="#FFF" d="M10.241 12.872l6.607-3.842-6.607-3.841z" />
        <g fill="#282828">
          <path d="M36.98 16.375c-.502-.342-.862-.872-1.073-1.593-.21-.72-.32-1.678-.32-2.876v-1.629c0-1.21.122-2.18.365-2.913.243-.733.623-1.266 1.137-1.6.514-.334 1.19-.505 2.027-.505.825 0 1.485.17 1.982.513.498.342.862.876 1.093 1.6.23.726.344 1.695.344 2.901v1.63c0 1.197-.114 2.158-.336 2.883-.223.726-.587 1.26-1.093 1.593-.501.334-1.185.506-2.047.506-.882.003-1.578-.168-2.08-.51zm2.82-1.76c.137-.366.21-.965.21-1.792v-3.5c0-.806-.069-1.392-.21-1.767-.138-.37-.385-.558-.737-.558-.34 0-.578.187-.72.558-.138.37-.21.961-.21 1.768v3.5c0 .83.064 1.429.198 1.791.133.367.376.55.728.55.356 0 .599-.183.74-.55zm35.805-2.68v.566c0 .721.02 1.259.065 1.621.04.359.13.624.263.787.133.163.34.248.619.248.376 0 .635-.146.773-.44.137-.293.214-.782.226-1.466l2.165.126c.012.098.016.232.016.403 0 1.04-.283 1.813-.846 2.326-.562.514-1.359.77-2.39.77-1.239 0-2.104-.39-2.602-1.173-.498-.782-.745-1.988-.745-3.625v-1.961c0-1.686.26-2.912.773-3.69.514-.774 1.396-1.161 2.646-1.161.862 0 1.521.159 1.983.476.46.318.785.81.975 1.483.186.672.283 1.601.283 2.782v1.923h-4.205v.004zm.32-4.725c-.126.159-.21.42-.255.778-.045.358-.065.908-.065 1.637v.807h1.837v-.807c0-.72-.024-1.267-.073-1.637-.048-.37-.137-.636-.263-.786-.125-.151-.323-.228-.59-.228-.267-.005-.466.077-.591.236zM30.87 11.723L28.014 1.34h2.492l1 4.705c.254 1.16.44 2.147.562 2.965h.073c.085-.586.27-1.568.562-2.949l1.036-4.725h2.492l-2.889 10.387v4.982H30.87v-4.982zm19.768-6.245v11.223h-1.962l-.218-1.373h-.053c-.534 1.039-1.335 1.556-2.4 1.556-.74 0-1.286-.244-1.638-.733-.352-.489-.526-1.25-.526-2.29V5.479h2.509v8.241c0 .501.052.86.162 1.072.109.211.29.321.546.321.218 0 .429-.065.627-.2.198-.134.348-.305.445-.512V5.477h2.508zm12.874 0v11.223H61.55l-.219-1.373h-.052c-.534 1.039-1.335 1.556-2.4 1.556-.74 0-1.286-.244-1.638-.733-.352-.489-.526-1.25-.526-2.29V5.479h2.509v8.241c0 .501.052.86.161 1.072.11.211.292.321.547.321.218 0 .429-.065.627-.2.198-.134.348-.305.445-.512V5.477h2.508z" />
          <path d="M57.46 3.373h-2.493v13.328h-2.455V3.373h-2.493V1.34h7.437v2.033h.004zm14.358 3.902c-.153-.709-.396-1.222-.736-1.54-.34-.318-.805-.476-1.4-.476-.461 0-.89.13-1.29.395-.401.26-.709.607-.927 1.034h-.02V.774h-2.42v15.931h2.072l.255-1.063h.052c.194.379.486.676.874.896.389.22.818.33 1.29.33.85 0 1.473-.395 1.874-1.181.4-.786.599-2.017.599-3.69v-1.778c.004-1.255-.073-2.236-.223-2.945zm-2.302 4.579c0 .818-.032 1.458-.1 1.922-.066.465-.179.795-.337.99a.77.77 0 01-.635.293c-.206 0-.396-.048-.574-.146a1.131 1.131 0 01-.43-.44V8.097a1.6 1.6 0 01.438-.75c.206-.195.428-.293.671-.293a.68.68 0 01.591.301c.138.2.235.542.291 1.015.053.476.081 1.153.081 2.032v1.45h.004z" />
        </g>
      </g>
    </svg>
  );
}

interface IYouTubeVideoCarouselProps {
  videos: IYouTubeVideoFieldsFragment[];
  onSelectVideo: (video: IYouTubeVideoFieldsFragment) => void;
}

function YouTubeVideoCarousel({ videos, onSelectVideo }: IYouTubeVideoCarouselProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageLimit = 3;
  const totalPages = Math.ceil(videos.length / pageLimit);
  const currentVideos = videos.slice(0, currentPage * 3);
  const hasMoreVideos = currentPage < totalPages;

  return (
    <div className="relative space-y-4">
      <div className="grid w-full grid-cols-3 gap-5">
        {currentVideos.map((video) => {
          return (
            <button
              type="button"
              key={video.id}
              onClick={() => onSelectVideo(video)}
              aria-label={`Selecionar video ${video.title}`}
              className="m-0 cursor-pointer appearance-none rounded-md border-none p-0 hover:opacity-80"
            >
              <img
                alt={video.title}
                title={`${video.title} • Eduardo`}
                width={187}
                height={105}
                src={video.thumbnails.hqdefault}
                className="h-[105px] w-[187px] select-none rounded-md object-cover"
              />
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex w-full justify-center">
          <button
            type="button"
            className="inline-flex font-medium text-blue-600 hover:underline"
            onClick={() => {
              if (hasMoreVideos) {
                setCurrentPage((previous) => previous + 1);
              } else {
                setCurrentPage(1);
              }
            }}
          >
            {hasMoreVideos ? (
              <FormattedMessage
                defaultMessage="Ver mais vídeos"
                description="Vídeos de análise no youtube"
                id="+gF+Ha"
              />
            ) : (
              <FormattedMessage defaultMessage="Recolher" description="Vídeos de análise no youtube" id="m3N3Mh" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
